import React, { useState } from 'react';
import { ReactComponent as MyIconF } from '../m.svg';
import { ReactComponent as MyIconM } from '../f.svg';
import {
    Card,
    CardMedia,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Pagination,
    Box,
    Grid,
    Chip,
    Stack,
    useTheme,
    alpha,
    Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CakeIcon from '@mui/icons-material/Cake';
import BadgeIcon from '@mui/icons-material/Badge';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 30px 0 ${alpha(theme.palette.common.black, 0.1)}`,
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(3),
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 1)} 100%)`,
    },
}));

const MatchesList = ({ matches }) => {
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const usersPerPage = 6;

    const totalPages = Math.ceil(matches.length / usersPerPage);
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = matches.slice(indexOfFirstUser, indexOfLastUser);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleViewProfile = (user) => {
        setSelectedUser(user);
    };

    const handleCloseModal = () => {
        setSelectedUser(null);
    };

    return (
        <Box sx={{ mt: 4 }}>
            {matches.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Grid container spacing={3}>
                        {currentUsers.map((user) => (
                            <Grid item xs={6}  key={user.uid}>
                                <Fade in timeout={500}>
                                    <StyledCard>
                                        <Box sx={{ position: 'relative' }}>
                                            {user.image_url ? (
                                                <CardMedia
                                                    component="img"
                                                    height="280"
                                                    image={user.image_url}
                                                    alt={`${user.first_name} ${user.last_name}`}
                                                    sx={{ objectFit: 'cover' }}
                                                />
                                            ) : user.gender === 'Male' ? (
                                                <Box sx={{ 
                                                    height: 280, 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center',
                                                    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                                }}>
                                                    <MyIconM width="150" height="150" />
                                                </Box>
                                            ) : (
                                                <Box sx={{ 
                                                    height: 280, 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center',
                                                    background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                                                }}>
                                                    <MyIconF width="150" height="150" />
                                                </Box>
                                            )}
                                          
                                        </Box>

                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    mb: 2,
                                                    fontWeight: 600,
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    backgroundClip: 'text',
                                                    WebkitBackgroundClip: 'text',
                                                    color: 'transparent',
                                                }}
                                            >
                                                {user.first_name} {user.last_name}
                                            </Typography>
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CakeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.age} years
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocationOnIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.state}, {user.district}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <BadgeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        UID: {user.uid}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={() => handleViewProfile(user)}
                                                sx={{ 
                                                    mt: 3,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    '&:hover': {
                                                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                                    }
                                                }}
                                            >
                                                View Profile
                                            </Button>
                                        </CardContent>
                                    </StyledCard>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>

                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            sx={{ 
                                mt: 4,
                                '& .MuiPaginationItem-root': {
                                    borderRadius: 2,
                                },
                                '& .Mui-selected': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    color: 'white',
                                    '&:hover': {
                                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            )}

            <StyledDialog 
                open={!!selectedUser} 
                onClose={handleCloseModal} 
                fullWidth 
                maxWidth="sm"
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    pb: 2,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {selectedUser?.first_name} {selectedUser?.last_name}
                    </Typography>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{ 
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {selectedUser && (
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                mb: 3,
                                position: 'relative'
                            }}>
                                {selectedUser.image_url ? (
                                    <img
                                        src={selectedUser.image_url}
                                        alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                                        style={{ 
                                            width: '200px', 
                                            height: '200px', 
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        }}
                                    />
                                ) : selectedUser.gender === 'Male' ? (
                                    <Box sx={{ 
                                        width: 200, 
                                        height: 200, 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        borderRadius: '50%',
                                        background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}>
                                        <MyIconM width="120" height="120" />
                                    </Box>
                                ) : (
                                    <Box sx={{ 
                                        width: 200, 
                                        height: 200, 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        borderRadius: '50%',
                                        background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                                        border: `3px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                    }}>
                                        <MyIconF width="120" height="120" />
                                    </Box>
                                )}
                                <Chip
                                    label={selectedUser.gender}
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        backgroundColor: selectedUser.gender === 'Male' ? 'primary.main' : 'secondary.main',
                                        color: 'white',
                                        fontWeight: 600,
                                    }}
                                />
                            </Box>

                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BadgeIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="body1">
                                        <strong>Full Name:</strong> {`${selectedUser.prefix} ${selectedUser.first_name} ${selectedUser.middle_name || ''} ${selectedUser.last_name}`}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CakeIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="body1">
                                        <strong>Age:</strong> {selectedUser.age} years
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOnIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="body1">
                                        <strong>Address:</strong> {`${selectedUser.address}, ${selectedUser.area}, ${selectedUser.state}, ${selectedUser.district} - ${selectedUser.pincode}`}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BadgeIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="body1">
                                        <strong>UID:</strong> {selectedUser.uid}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
            </StyledDialog>
        </Box>
    );
};

export default MatchesList;
